import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { HiArrowDown, HiArrowLeft, HiExternalLink, HiInformationCircle, HiMinus, HiVolumeOff, HiX } from "react-icons/hi";
import ChatAvatar from "./ChatAvatar";
import ChatModal, { modalButton } from "./ChatModal";
import Composer from "./Composer";
import ConversationInfoModal from "./ConversationInfoModal";
import AttachmentViewer from "./AttachmentViewer";
import PinnedBar from "./PinnedBar";
import MessageInfoModal from "./MessageInfoModal";
import MessageItem from "./MessageItem";
import { useChat } from "./ChatProvider";
import { formatDayLabel, formatLastSeen, isSameDay, isViewable, otherMemberId, userName } from "./chatUtils";

const GROUP_WINDOW_MS = 5 * 60 * 1000;
const NEAR_BOTTOM_PX = 120;
const MAX_FOCUS_PAGES = 20;

function TypingIndicator({ userIds }) {
  const { users } = useChat();
  if (!userIds.length) return null;
  const names = userIds.map((id) => userName(users[id]).split(" ")[0]);
  const label =
    names.length === 1 ? `${names[0]} is typing` : names.length === 2 ? `${names.join(" and ")} are typing` : "Several people are typing";
  return (
    <div className="flex items-center gap-2 px-4 py-1 text-xs text-gray-500" aria-live="polite">
      <span className="flex gap-0.5">
        {[0, 150, 300].map((delay) => (
          <span key={delay} className="w-1.5 h-1.5 rounded-full bg-gray-400 animate-bounce" style={{ animationDelay: `${delay}ms` }} />
        ))}
      </span>
      {label}…
    </div>
  );
}

// A conversation: header, scrolling message history and the composer.
// variant "dock" renders the compact docked-window chrome; "page" fills the chat page.
// `share`: { text, files } shared from another app, put into the composer once (then onShareUsed).
function ConversationView({ conversationId, variant = "page", minimized = false, onClose, onMinimize, onExpand, onBack, share, onShareUsed }) {
  const chat = useChat();
  const { me, users, conversations, messages, typing, focus, status, titleOf } = chat;
  const { ensureMessages, loadOlder, getLoadedMessages, markRead, registerVisible, deleteMessage, clearFocus } = chat;
  const conversation = conversations[conversationId];
  const page = messages[conversationId];
  const items = useMemo(() => page?.items || [], [page]);

  const scrollRef = useRef(null);
  const nearBottomRef = useRef(true);
  const scrollSnapshot = useRef(null);
  const prevEdges = useRef({ first: null, last: null });
  const [showJump, setShowJump] = useState(false);
  const [replyTo, setReplyTo] = useState(null);
  const [editing, setEditing] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [showInfo, setShowInfo] = useState(false);
  const [highlightId, setHighlightId] = useState(null);
  const [dropped, setDropped] = useState(null);
  const [dragging, setDragging] = useState(false);
  const [viewer, setViewer] = useState(null); // { id, fallback } – attachment open in the viewer
  const [infoMessage, setInfoMessage] = useState(null); // own message whose read status is shown

  // Every image and PDF in the loaded history, in order, so the viewer can page through them.
  const viewableMedia = useMemo(
    () =>
      items
        .filter((m) => !m.deletedAt && !m.pending)
        .flatMap((m) => (m.attachments || []).filter((a) => a.id && isViewable(a.mimeType)).map((attachment) => ({ attachment, message: m }))),
    [items]
  );
  const openAttachment = useCallback(
    (attachment) => {
      const message = items.find((m) => (m.attachments || []).some((a) => a === attachment || (a.id && a.id === attachment.id)));
      setViewer({ id: attachment.id, fallback: [{ attachment, message: message || { senderId: me?.id, createdAt: new Date().toISOString() } }] });
    },
    [items, me]
  );

  // Direct-chat header bio: two lines by default, "Show more" when it doesn't fit.
  const otherBio =
    conversation?.type === "direct" ? users[otherMemberId(conversation, me?.id)]?.bio || null : null;
  const bioRef = useRef(null);
  const [bioExpanded, setBioExpanded] = useState(false);
  const [bioOverflows, setBioOverflows] = useState(false);
  useLayoutEffect(() => {
    const el = bioRef.current;
    if (!el || bioExpanded) return;
    setBioOverflows(el.scrollHeight > el.clientHeight + 1);
  }, [otherBio, bioExpanded]);

  useEffect(() => {
    ensureMessages(conversationId);
  }, [conversationId, ensureMessages]);

  useEffect(() => {
    if (!share) return;
    setDropped({ files: share.files, text: share.text, nonce: Date.now() });
    onShareUsed?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [share]);

  // While on screen, incoming messages count as read and don't beep.
  useEffect(() => {
    if (minimized) return undefined;
    return registerVisible(conversationId);
  }, [conversationId, minimized, registerVisible]);

  const lastConfirmed = useMemo(() => [...items].reverse().find((m) => !m.pending), [items]);

  const markLatestRead = useCallback(() => {
    if (minimized || document.visibilityState !== "visible" || !lastConfirmed) return;
    markRead(conversationId, lastConfirmed.id);
  }, [minimized, lastConfirmed, markRead, conversationId]);

  useEffect(() => {
    markLatestRead();
    const onVisible = () => markLatestRead();
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", onVisible);
    return () => {
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", onVisible);
    };
  }, [markLatestRead]);

  const scrollToBottom = (smooth) => {
    const el = scrollRef.current;
    if (el) el.scrollTo({ top: el.scrollHeight, behavior: smooth ? "smooth" : "auto" });
    setShowJump(false);
  };

  // Keep the view anchored: stay at the bottom for new messages, keep position when older ones load.
  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const first = items[0];
    const last = items[items.length - 1];
    const prev = prevEdges.current;
    const firstKey = first?.id ?? first?.clientId;
    const lastKey = last?.id ?? last?.clientId;

    if (prev.first === null && items.length) {
      el.scrollTop = el.scrollHeight;
    } else if (scrollSnapshot.current !== null && firstKey !== prev.first) {
      el.scrollTop = el.scrollHeight - scrollSnapshot.current;
    } else if (lastKey !== prev.last && items.length) {
      if (nearBottomRef.current || last.senderId === me?.id) el.scrollTop = el.scrollHeight;
      else setShowJump(true);
    }
    scrollSnapshot.current = null;
    prevEdges.current = { first: items.length ? firstKey : null, last: lastKey };
  }, [items, me]);

  // Typing indicator and reactions change height; keep pinned if already at the bottom.
  const typers = Object.keys(typing[conversationId] || {}).map(Number).filter((id) => id !== me?.id);
  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (el && nearBottomRef.current) el.scrollTop = el.scrollHeight;
  }, [typers.length]);

  const hasConversation = Boolean(conversation);
  // The keyboard opening (or the composer growing) shrinks the history; stay on the latest message.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el || typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(() => {
      if (nearBottomRef.current) el.scrollTop = el.scrollHeight;
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [minimized, hasConversation]);

  const requestOlder = useCallback(() => {
    const el = scrollRef.current;
    if (!el || !page?.hasMore || page.loading) return;
    scrollSnapshot.current = el.scrollHeight - el.scrollTop;
    loadOlder(conversationId);
  }, [page, loadOlder, conversationId]);

  const onScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    nearBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < NEAR_BOTTOM_PX;
    if (nearBottomRef.current) setShowJump(false);
    if (el.scrollTop < 80) requestOlder();
  };

  // Jump to a message picked from search: page back until it's loaded, then scroll to and flash it.
  useEffect(() => {
    if (!focus || focus.conversationId !== conversationId || !page?.loaded) return undefined;
    let cancelled = false;
    (async () => {
      const target = focus.messageId;
      const loaded = getLoadedMessages(conversationId);
      let found = loaded.some((m) => m.id === target);
      let before = loaded.find((m) => !m.pending)?.id;
      for (let attempt = 0; !found && !cancelled && before && attempt < MAX_FOCUS_PAGES; attempt += 1) {
        // eslint-disable-next-line no-await-in-loop
        const result = await loadOlder(conversationId, before);
        if (!result) break;
        found = result.messages.some((m) => m.id === target);
        before = result.messages[0]?.id;
        if (!result.hasMore) break;
      }
      // Wait for the loaded page to render, then scroll to the message and flash it.
      for (let tries = 0; !cancelled && tries < 20; tries += 1) {
        const el = document.getElementById(`chat-msg-${target}`);
        if (el && scrollRef.current?.contains(el)) {
          nearBottomRef.current = false;
          el.scrollIntoView({ block: "center", behavior: "smooth" });
          setHighlightId(target);
          setTimeout(() => setHighlightId(null), 2500);
          break;
        }
        // eslint-disable-next-line no-await-in-loop
        await new Promise((resolve) => setTimeout(resolve, 100));
      }
      if (!cancelled) clearFocus();
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focus, page?.loaded, conversationId]);

  const editLast = () => {
    const mine = [...items].reverse().find((m) => m.senderId === me?.id && !m.pending && !m.deletedAt && m.type === "text");
    if (mine) setEditing(mine);
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    if (e.dataTransfer.files?.length) setDropped({ files: e.dataTransfer.files, nonce: Date.now() });
  };

  if (!conversation) return null;

  const isGroup = conversation.type === "group";
  const other = isGroup ? null : users[otherMemberId(conversation, me?.id)];
  const title = titleOf(conversation);
  const subtitle = typers.length
    ? "typing…"
    : isGroup
      ? `${conversation.members.length} members`
      : formatLastSeen(other);

  // Read receipt under my latest message, from other members' read positions.
  const lastMessage = items[items.length - 1];
  let receipt = null;
  if (lastMessage && lastMessage.senderId === me?.id && !lastMessage.pending && lastMessage.type === "text") {
    const readers = conversation.members.filter((m) => m.userId !== me?.id && (m.lastReadMessageId || 0) >= lastMessage.id);
    if (!isGroup) receipt = { label: readers.length ? "Seen" : "Sent" };
    else if (readers.length) {
      const names = readers.map((m) => userName(users[m.userId]));
      receipt = {
        label: readers.length === conversation.members.length - 1 ? "Seen by everyone" : `Seen by ${readers.length}`,
        title: names.join(", "),
        readers: readers.map((m) => m.userId),
      };
    } else receipt = { label: "Sent" };
  }

  const isDock = variant === "dock";
  const iconButton = "p-1.5 rounded-md hover:bg-black/10 dark:hover:bg-white/10";

  const titleBlock = (
    <>
      <ChatAvatar
        name={title}
        src={isGroup ? conversation.avatarThumbUrl : other?.avatarThumbUrl}
        seed={isGroup ? conversation.id : other?.id}
        group={isGroup}
        size={isDock ? "sm" : "md"}
        online={isGroup ? undefined : !!other?.online}
      />
      <span className="min-w-0 flex-1 text-left">
        {isGroup ? (
          <>
            <span className={`block truncate font-semibold ${isDock ? "text-sm" : "text-base text-gray-800 dark:text-gray-100"}`}>{title}</span>
            {!minimized && (
              <span className={`block truncate text-xs ${isDock ? "text-white/80" : typers.length ? "text-blue-600" : "text-gray-500"}`}>{subtitle}</span>
            )}
          </>
        ) : (
          <>
            {/* Name and online status on one row, bio underneath. */}
            <span className="flex items-center gap-2 min-w-0">
              <span className={`truncate font-semibold ${isDock ? "text-sm" : "text-base text-gray-800 dark:text-gray-100"}`}>{title}</span>
              <span
                className={`flex shrink-0 items-center gap-1 text-[11px] font-medium ${
                  isDock ? "text-white/85" : typers.length ? "text-blue-600" : other?.online ? "text-green-600 dark:text-green-400" : "text-gray-500"
                }`}
                data-tour="chat-header-status"
              >
                {!typers.length && <span className={`h-1.5 w-1.5 rounded-full ${other?.online ? "bg-green-400" : "bg-gray-400"}`} aria-hidden="true" />}
                {subtitle}
              </span>
            </span>
            {otherBio && (
              <span className={`block text-xs ${isDock ? "text-white/80" : "text-gray-500 dark:text-gray-400"}`}>
                <span
                  ref={bioRef}
                  className={`whitespace-pre-wrap break-words ${bioExpanded ? "block max-h-32 overflow-y-auto pr-1" : "line-clamp-2"}`}
                  data-tour="chat-header-bio"
                >
                  {otherBio}
                </span>
                {(bioOverflows || bioExpanded) && (
                  <button
                    type="button"
                    onClick={() => setBioExpanded((v) => !v)}
                    className={`mt-0.5 text-[11px] font-semibold hover:underline ${isDock ? "text-white" : "text-[#2470B7] dark:text-blue-400"}`}
                    aria-expanded={bioExpanded}
                  >
                    {bioExpanded ? "Show less" : "Show more"}
                  </button>
                )}
              </span>
            )}
          </>
        )}
      </span>
    </>
  );

  const header = (
    <div
      className={`flex items-center gap-2 px-3 py-2 shrink-0 ${
        isDock ? "min-h-14 bg-gradient-to-r from-[#2470B7] to-[#1d5a93] text-white" : "min-h-16 border-b border-gray-200 dark:border-gray-700"
      }`}
    >
      {onBack && (
        <button type="button" className={`${iconButton} -ml-1`} onClick={onBack} aria-label="Back to chats">
          <HiArrowLeft className="w-5 h-5" />
        </button>
      )}
      <div className="flex items-center gap-2 min-w-0 flex-1">{titleBlock}</div>
      {conversation.muted && <HiVolumeOff className="w-4 h-4 opacity-70" title="Muted" />}
      {minimized && conversation.unreadCount > 0 && (
        <span className="min-w-[20px] h-5 px-1.5 rounded-full bg-white text-[#2470B7] text-[11px] font-bold flex items-center justify-center">
          {conversation.unreadCount}
        </span>
      )}
      <div className="flex items-center">
        {!minimized && (
          <button type="button" className={iconButton} onClick={() => setShowInfo(true)} title="Details" aria-label="Conversation details" data-tour="chat-details">
            <HiInformationCircle className="w-5 h-5" />
          </button>
        )}
        {isDock && onExpand && (
          <button type="button" className={iconButton} onClick={onExpand} title="Open in full view" aria-label="Open in full view">
            <HiExternalLink className="w-5 h-5" />
          </button>
        )}
        {isDock && !onBack && (
          <button type="button" className={iconButton} onClick={onMinimize} title={minimized ? "Expand" : "Minimize"} aria-label={minimized ? "Expand chat" : "Minimize chat"}>
            <HiMinus className="w-5 h-5" />
          </button>
        )}
        {isDock && (
          <button type="button" className={iconButton} onClick={onClose} title="Close" aria-label="Close chat">
            <HiX className="w-5 h-5" />
          </button>
        )}
      </div>
    </div>
  );

  return (
    <div className="chat-ui flex flex-col h-full min-h-0 bg-white dark:bg-gray-900">
      {header}
      {!minimized && status !== "connected" && (
        <div className="shrink-0 bg-amber-50 px-3 py-1.5 text-center text-xs font-medium text-amber-800 dark:bg-amber-500/10 dark:text-amber-300" role="status">
          {navigator.onLine === false
            ? "You're offline. Messages will send when you're back online."
            : status === "connecting"
              ? "Connecting…"
              : "Reconnecting…"}
        </div>
      )}
      {!minimized && <PinnedBar conversationId={conversationId} compact={isDock} />}
      {!minimized && (
        <>
          <div
            className="relative flex-1 min-h-0"
            onDragOver={(e) => {
              if (e.dataTransfer.types?.includes("Files")) {
                e.preventDefault();
                setDragging(true);
              }
            }}
            onDragLeave={(e) => {
              if (!e.currentTarget.contains(e.relatedTarget)) setDragging(false);
            }}
            onDrop={onDrop}
          >
            <div ref={scrollRef} onScroll={onScroll} className="h-full overflow-y-auto overscroll-contain py-2">
              {page?.loading && <p className="text-center text-xs text-gray-400 py-2">Loading…</p>}
              {page?.loaded && !page.hasMore && (isGroup || !other) && (
                <p className="text-center text-xs text-gray-400 py-3">
                  {isGroup ? `This is the beginning of ${title}` : `This is the beginning of your chat with ${title}`}
                </p>
              )}
              {page?.loaded && !page.hasMore && !isGroup && other && (
                <div className="flex flex-col items-center text-center gap-1 px-6 pt-4 pb-3" data-tour="chat-intro">
                  <ChatAvatar name={title} src={other.avatarUrl || other.avatarThumbUrl} seed={other.id} size="lg" online={!!other.online} />
                  <p className="mt-1 text-sm font-semibold text-gray-800 dark:text-gray-100">
                    {title}
                    <span className={`ml-2 text-xs font-medium ${other.online ? "text-green-600 dark:text-green-400" : "text-gray-500"}`}>
                      {formatLastSeen(other)}
                    </span>
                  </p>
                  {(other.jobTitle || other.department) && (
                    <p className="text-xs text-gray-500">{[other.jobTitle, other.department].filter(Boolean).join(" · ")}</p>
                  )}
                  {other.bio && <p className="max-w-xs whitespace-pre-wrap break-words text-xs text-gray-600 dark:text-gray-300">{other.bio}</p>}
                  <p className="mt-1 text-[11px] text-gray-400">This is the beginning of your chat with {title}</p>
                </div>
              )}
              {items.map((message, index) => {
                const prev = items[index - 1];
                const newDay = !prev || !isSameDay(prev.createdAt, message.createdAt);
                const showHeader =
                  newDay ||
                  !prev ||
                  prev.type === "system" ||
                  prev.senderId !== message.senderId ||
                  new Date(message.createdAt) - new Date(prev.createdAt) > GROUP_WINDOW_MS;
                return (
                  <div key={message.id ?? message.clientId}>
                    {newDay && (
                      <div className="flex items-center gap-3 px-4 my-3 text-[11px] font-medium text-gray-400">
                        <span className="flex-1 h-px bg-gray-200 dark:bg-gray-700" />
                        {formatDayLabel(message.createdAt)}
                        <span className="flex-1 h-px bg-gray-200 dark:bg-gray-700" />
                      </div>
                    )}
                    <MessageItem
                      message={message}
                      showHeader={showHeader}
                      compact={isDock}
                      highlighted={highlightId === message.id}
                      receipt={message === lastMessage ? receipt : null}
                      onReply={(m) => {
                        setEditing(null);
                        setReplyTo(m);
                      }}
                      onEdit={(m) => {
                        setReplyTo(null);
                        setEditing(m);
                      }}
                      onDelete={setConfirmDelete}
                      onOpenAttachment={openAttachment}
                      onInfo={isGroup ? setInfoMessage : undefined}
                    />
                  </div>
                );
              })}
              <TypingIndicator userIds={typers} />
            </div>
            {showJump && (
              <button
                type="button"
                onClick={() => scrollToBottom(true)}
                className="absolute bottom-3 left-1/2 -translate-x-1/2 flex items-center gap-1 px-3 py-1.5 rounded-full bg-[#2470B7] text-white text-xs font-medium shadow-lg"
              >
                <HiArrowDown className="w-4 h-4" /> New messages
              </button>
            )}
            {dragging && (
              <div className="absolute inset-2 rounded-xl border-2 border-dashed border-blue-400 bg-blue-50/90 dark:bg-gray-800/90 flex items-center justify-center text-sm font-medium text-blue-700 dark:text-blue-300 pointer-events-none">
                Drop files to attach
              </div>
            )}
          </div>
          <Composer
            conversationId={conversationId}
            replyTo={replyTo}
            onCancelReply={() => setReplyTo(null)}
            editing={editing}
            onCancelEdit={() => setEditing(null)}
            onEditLast={editLast}
            droppedFiles={dropped}
            disabled={!me}
            offline={status !== "connected"}
          />
        </>
      )}

      {showInfo && <ConversationInfoModal conversationId={conversationId} onClose={() => setShowInfo(false)} />}
      {infoMessage && <MessageInfoModal conversationId={conversationId} message={infoMessage} onClose={() => setInfoMessage(null)} />}
      {viewer && (
        <AttachmentViewer
          items={viewableMedia.some((m) => m.attachment.id === viewer.id) ? viewableMedia : viewer.fallback}
          startId={viewer.id}
          onClose={() => setViewer(null)}
        />
      )}
      {confirmDelete && (
        <ChatModal
          title="Delete message?"
          onClose={() => setConfirmDelete(null)}
          footer={
            <>
              <button type="button" className={modalButton.secondary} onClick={() => setConfirmDelete(null)}>Cancel</button>
              <button
                type="button"
                className={modalButton.danger}
                onClick={() => {
                  deleteMessage(confirmDelete.id).catch(() => {});
                  setConfirmDelete(null);
                }}
              >
                Delete
              </button>
            </>
          }
        >
          <p className="text-sm">This message and its attachments will be removed for everyone in the chat.</p>
        </ChatModal>
      )}
    </div>
  );
}

export default ConversationView;
